import moment from 'moment-timezone';
import schedule from './market-schedule.json';

type FullDayClosure = { date: string; name: string };
type EarlyClose = { date: string; closeTimeET: string; name: string };
type MarketSchedule = {
  fullDayClosures: FullDayClosure[];
  earlyCloses: EarlyClose[];
};
const marketSchedule = schedule as MarketSchedule;

const ET = 'America/New_York';

const fmtDate = (m: moment.Moment) => m.format('YYYY-MM-DD');

function findClosure(date: string): FullDayClosure | undefined {
  return marketSchedule.fullDayClosures.find(c => c.date === date);
}

function findEarlyClose(date: string): EarlyClose | undefined {
  return marketSchedule.earlyCloses.find(e => e.date === date);
}

function isClosed(date: string): boolean {
  return !!findClosure(date);
}

function isOpenET(nowET: moment.Moment) {
  const today = fmtDate(nowET);
  const day = nowET.day();

  if (day === 0 || day === 6) return false;
  if (isClosed(today)) return false;

  const openET = nowET.clone().hour(9).minute(30).second(0);
  const early = findEarlyClose(today);
  const closeH = early ? +early.closeTimeET.split(':')[0] : 16;
  const closeM = early ? +early.closeTimeET.split(':')[1] : 0;
  const closeET = nowET.clone().hour(closeH).minute(closeM).second(0);

  return nowET.isSameOrAfter(openET) && nowET.isBefore(closeET);
}

function findNextOpenET(nowET: moment.Moment) {
  for (let i = 0; i < 14; i++) {
    const cand = nowET.clone().startOf('day').add(i, 'days').hour(9).minute(30).second(0);
    const dayOfWeek = cand.day();
    const dateStr = fmtDate(cand);

    if (dayOfWeek === 0 || dayOfWeek === 6) continue;
    if (isClosed(dateStr)) continue;
    if (i === 0 && nowET.isAfter(cand)) continue;

    return cand;
  }

  return nowET.clone().add(1, 'day').hour(9).minute(30).second(0);
}

function toLocal(m: moment.Moment) {
  return m.clone().local().format('dddd, HH:mm');
}

function fmtCountdown(target: moment.Moment) {
  const diff = target.valueOf() - moment().valueOf();
  if (diff <= 0) return '00:00:00';
  const dur = moment.duration(diff);
  const h = String(Math.floor(dur.asHours())).padStart(2, '0');
  const m = String(dur.minutes()).padStart(2, '0');
  const s = String(dur.seconds()).padStart(2, '0');
  return `${h}:${m}:${s}`;
}

type BookmarkHint =
  | { kind: 'bookmark'; text: string }
  | { kind: 'share' };

function getBookmarkHint(): BookmarkHint | null {
  const ua = navigator.userAgent || '';
  const uaData = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData;
  const platform = uaData?.platform || navigator.platform || '';
  const maxTouchPoints = navigator.maxTouchPoints || 0;

  const isIOS = /iPhone|iPad|iPod/i.test(ua) || (platform === 'MacIntel' && maxTouchPoints > 1);
  const isAndroid = /Android/i.test(ua);
  const isMac = /Mac/i.test(platform) && !isIOS;
  const isWindows = /Win/i.test(platform);
  const isLinux = /Linux/i.test(platform) && !isAndroid;
  if (isMac) return { kind: 'bookmark', text: '🔖 Bookmark this page: press <strong><code>Cmd + D</code></strong>' };
  if (isWindows || isLinux) return { kind: 'bookmark', text: '🔖 Bookmark this page: press <strong><code>Ctrl + D</code></strong>' };
  if (isIOS || isAndroid) return { kind: 'share' };
  return null;
}

async function handleShareClick(button: HTMLButtonElement) {
  const shareUrl = 'https://is-market-open.com/';
  const copyUrl = 'is-market-open.com';
  const title = document.title;

  if (navigator.share) {
    try {
      await navigator.share({ title, url: shareUrl });
      return;
    } catch {
      // Fall through to clipboard.
    }
  }

  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(copyUrl);
      button.textContent = '✅ Link copied';
      window.setTimeout(() => {
        button.textContent = '🔗 Share this page';
      }, 2000);
      return;
    } catch {
      // Fall through to no-op.
    }
  }

  button.textContent = '🔗 Share this page';
}

function setShareHint(bookmarkHint: HTMLElement) {
  if (bookmarkHint.dataset.mode === 'share') return;
  bookmarkHint.dataset.mode = 'share';
  bookmarkHint.innerHTML = '<button type="button" class="share-link">🔗 Share this page</button>';
  const button = bookmarkHint.querySelector<HTMLButtonElement>('.share-link');
  if (button) {
    button.addEventListener('click', () => {
      handleShareClick(button);
    });
  }
}

function updateUI() {
  const now = moment();
  const nowET = now.clone().tz(ET);
  const today = fmtDate(nowET);
  const day = nowET.day();
  const early = findEarlyClose(today);
  const closure = findClosure(today);

  const status = document.getElementById('status')!;
  const note = document.getElementById('note')!;
  const next = document.getElementById('next')!;
  const timer = document.getElementById('countdown')!;
  const localTm = document.getElementById('local-time')!;
  const bookmarkHint = document.getElementById('bookmark-hint')!;

  const userTZ = moment.tz.guess();
  localTm.textContent = `⏱ Your local time: ${now.format('HH:mm')} (${userTZ})`;

  if (day === 0 || day === 6) {
    note.innerHTML = '<b>Note:</b> Today is a weekend.';
  } else if (closure) {
    note.innerHTML = `<b>Note:</b> Today is a market holiday (${closure.name}).`;
  } else if (early) {
    note.innerHTML = `<b>Note:</b> Today is an early close day (${early.name}). Markets close at 1:00 PM ET.`;
  } else {
    note.textContent = '';
  }

  if (isOpenET(nowET)) {
    status.textContent = '✅ The market is OPEN.';
    status.className = 'status status--open';

    const closeET = nowET.clone()
      .hour(early ? +early.closeTimeET.split(':')[0] : 16)
      .minute(early ? +early.closeTimeET.split(':')[1] : 0)
      .second(0);

    next.innerHTML = `<b>Closes at:</b> ${toLocal(closeET)} your time.`;
    timer.innerHTML = `Time until close: ${fmtCountdown(closeET)}`;
  } else {
    status.textContent = '❌ The market is CLOSED.';
    status.className = 'status status--closed';

    const nextOpen = findNextOpenET(nowET);
    next.innerHTML = `<b>Next open:</b> ${toLocal(nextOpen)} your time.`;
    timer.innerHTML = `<b>Time until open:</b> ${fmtCountdown(nextOpen)}`;
  }

  const lastUpdated = document.getElementById('last-updated')!;
  lastUpdated.textContent = `⏳ Last updated: ${nowET.format('MMMM D, YYYY h:mm:ss A')} ET`;

  const hint = getBookmarkHint();
  if (hint?.kind === 'bookmark') {
    bookmarkHint.dataset.mode = 'bookmark';
    bookmarkHint.classList.remove('bookmark-hint--share');
    bookmarkHint.innerHTML = hint.text;
    bookmarkHint.style.display = 'block';
  } else if (hint?.kind === 'share') {
    bookmarkHint.classList.add('bookmark-hint--share');
    setShareHint(bookmarkHint);
    bookmarkHint.style.display = 'block';
  } else {
    bookmarkHint.textContent = '';
    bookmarkHint.classList.remove('bookmark-hint--share');
    bookmarkHint.style.display = 'none';
    delete bookmarkHint.dataset.mode;
  }
}

updateUI();
setInterval(updateUI, 1000);

// ── Feedback form ──

declare global {
  interface Window {
    turnstile?: {
      reset: (widgetId?: string) => void;
    };
  }
}

function initFeedbackForm() {
  const form = document.getElementById('feedback-form') as HTMLFormElement | null;
  const textarea = document.getElementById('feedback-text') as HTMLTextAreaElement | null;
  const errorEl = document.getElementById('feedback-error');
  const thanksEl = document.getElementById('feedback-thanks');
  if (!form || !textarea || !errorEl || !thanksEl) return;

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    errorEl.hidden = true;

    const widget = form.querySelector<HTMLInputElement>('[name="cf-turnstile-response"]');
    const token = widget ? widget.value : '';

    try {
      const res = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          feedback: textarea.value,
          'cf-turnstile-response': token
        })
      });

      if (res.ok) {
        form.hidden = true;
        thanksEl.hidden = false;
        return;
      }

      const data = (await res.json().catch(() => ({}))) as { error?: string };
      errorEl.textContent = data.error || 'Something went wrong. Please try again.';
      errorEl.hidden = false;
      window.turnstile?.reset();
    } catch {
      errorEl.textContent = 'Something went wrong. Please try again.';
      errorEl.hidden = false;
      window.turnstile?.reset();
    }
  });
}

initFeedbackForm();
